// Every string the player reads, in one table. Code says crew/impostor/kill/body/eject; the screen
// says driver/agbero/knock/breakdown/drop. Pidgin-flavoured, short, exclamation marks only on
// "Owa!" and "Wahala!", plain digits, no emoji.

import type { QuickChatId } from '@wahala/protocol';
import { parkNaira } from './lib/money';

export const S = {
  /** Staging only: the code recorded for this email (nothing is sent there). */
  testCode: (code: string) => `Staging: your code is ${code}`,
  testCodeFill: 'Fill it',
  /** Staging only: the recorded email carried no code (that email already has a profile). */
  testNoCode: (text: string) => `Staging: no code went to this email. It says: ${text}`,
  /** Read after a notice: tapping it closes it. */
  dismiss: '(tap to close)',
  appName: 'WAHALA',
  loading: 'Loading the park…',
  /** The way into a park (ui/parkLoad.ts): from the menu's tap to the street's first frame. */
  parkLoad: {
    /** The route bar's label while the park gives a seat (the street's is play.streetBar). */
    parkBar: 'Park',
    park: (code: string) => `Park ${code}`,
    solo: 'Play alone',
    tip: 'Tip',
    maps: {
      'lagos-route': 'Lagos Route',
      'third-mainland': 'Third Mainland',
      'oshodi-loop': 'Oshodi Loop',
      'lekki-sprint': 'Lekki Sprint',
      'apapa-port-run': 'Apapa Port Run',
    } as Record<string, string>,
    /** What runs now, one line per real stage. */
    lines: {
      find: 'Dey find your park…',
      solo: 'Dey call the other drivers…',
      gate: 'Dey open the park gate…',
      seat: 'Dey find your seat for the park…',
      street: 'The street dey load. Small time.',
      build: 'Dey build the street…',
      light: 'Dey light the road…',
    },
    /** Turned while it loads: three for the game type, then three for every park. */
    tips: {
      park: [
        'One driver na VIO in disguise. No dey drive alone.',
        'You see keke wey dem clamp? Report am sharp sharp.',
        'Blow the horn for the park make everybody come meeting.',
      ],
      powers: [
        'Pick the power-up for road, use am, waka pass.',
        'Boost, wiper or fake breakdown: use am for the right time.',
        'The VIO still dey among una. Watch who dey follow you.',
      ],
      hide: [
        'Everybody know the VIO and e dey come. Go hide.',
        'Do your stops when the VIO no dey near.',
        'Last till the clock finish and the drivers carry am.',
      ],
      race: [
        'Three laps of Third Mainland. First over the line carry am.',
        'Watch your fuel. Pick the fuel can before your tank dry.',
        'Dodge the potholes and the area boys. Dem no dey play.',
      ],
      any: [
        'Press anywhere for the left side and push. Your keke go follow your thumb.',
        'Your colour, your plate and your name na your ID for the park.',
        'Park money wey you win fit buy new loads and finishes for the shop.',
      ],
    },
  },
  /** A screen that broke (ui/Crash.tsx): the page is never left blank. */
  broken: {
    title: 'Wahala!',
    body: 'Something don scatter for this screen. Load the game again.',
    reload: 'Load am again',
  },
  outside: {
    title: 'Open am for Lagos Life',
    body: 'WAHALA plays inside the Lagos Life app. Open it there and find WAHALA under Games.',
  },
  login: {
    taken: 'Another driver don take that name. Try another one.',
    title: 'Who dey drive?',
    label: 'Your driver name',
    placeholder: 'e.g. Tunde',
    go: 'Enter the park',
    note: 'Staging build: a name is all you need.',
    guestNote: 'A name is all you need. This phone remembers you next time.',
  },
  parks: {
    title: 'Open parks',
    loading: 'Looking for open parks…',
    none: 'No open park right now. Quick play opens one for you.',
    name: (code: string) => `Park ${code}`,
    free: 'Free parks',
    stakes: 'Stake tables',
  },
  stakes: {
    loading: 'Looking for stake tables…',
    none: 'No stake table dey fill right now. Open one.',
    outside: 'Stake tables open inside Lagos Life. Play the game there to put fare.',
    note: 'Fare na Lagos Life game money. The winner carry the pot. Leave before the start and your fare go come back.',
    listTitle: 'Tables wey dey fill',
    seatCount: (seats: number) => `${seats} seats`,
    gameName: (game: 'park' | 'race') => (game === 'race' ? 'Race' : 'The Park'),
    open: 'Open a stake table',
    name: (code: string) => `Table ${code}`,
    cancel: 'Not now',
    title: 'Choose your fare',
    /** The fare picker's seat choice: 2 seats race head to head, 4 play The Park. */
    seatsTitle: 'How many seats?',
    seatsRace: '2 seats',
    seatsRaceGame: 'Race',
    seatsPark: '4 seats',
    seatsParkGame: 'The Park',
    /** A table card's line: the game it plays and its seats. */
    game: (game: 'park' | 'race', seats: number) => `${game === 'race' ? 'Race' : 'The Park'}, ${seats} seats`,
    /** The table's board in the stake park's lobby (PF-07). */
    board: {
      band: 'Stake table',
      code: 'Table code',
      codeHint: 'Your people fit find am for Stake tables.',
      fareIn: 'Fare dey inside',
      fareInLine: 'Lagos Life dey hold am',
      waiting: 'Drivers dey come',
      seated: (seated: number, seats: number) => `${seated} of ${seats} don sit`,
      left: (clock: string) => `${clock} left`,
      start: 'Round go start',
      startLine: 'Once every seat full',
      full: 'Table don full',
      fullLine: 'The round dey start',
      leave: 'Leave table',
      leaveLine: 'Leave before the round and your fare go come back.',
      // the time ran out (live): the fare is back, a free round is offered
      expired: 'Nobody else come',
      expiredLine: (fare: string) => `The table no full on time, so Lagos Life don return your ${fare} fare.`,
      free: 'Play a free round',
      freeLine: 'Drive the route for free while the next table fill. Watch out for the VIO.',
      back: 'Back',
      // staging only: solo players took the empty seats
      solo: 'Practice round',
      soloLine: 'The park don fill the empty seats. This round na for practice: Lagos Life don return your fare.',
    },
    voided: (code: string) => `Table ${code} no full on time. Lagos Life don return your fare.`,
    cancelled: (code: string) => `Table ${code} don cancel. Lagos Life don return your fare.`,
  },
  menu: {
    hello: (name: string) => `Welcome, ${name}`,
    helloAnon: 'Welcome to the park',
    joinTitle: 'Got a park code?',
    settings: 'Settings',
    create: 'Open a park',
    join: 'Join with code',
    joinShort: 'Join',
    quick: 'Quick play',
    quickNote: 'Jump into a park wey dey start now.',
    letAnyone: 'Let anyone join',
    codeLabel: 'Park code',
    codePlaceholder: 'ABC123',
    preset: 'Rules',
    back: 'Back',
    logOut: 'Log out',
    logOutNote: 'Come back with another name.',
    shop: 'Shop',
    /** The community chat's button on the menu bar (website players only). */
    chat: 'Chat',
    chatUnread: (n: number) => `Chat, ${n > 99 ? '99+' : n} new`,
    pass: 'Route Pass',
    held: (code: string) => `Your seat still dey for park ${code}.`,
    backToPark: 'Back to your park',
    backShort: 'Back to park',
    open: 'Menu',
    title: 'Menu',
    close: 'Close',
    shopNote: (n: string) => `${n} park money`,
    today: "Today's challenges",
    board: 'Rankings',
    history: 'Your rounds',
    sound: 'Sound',
    profile: 'Your profile',
    people: 'Your people',
    invited: (name: string) => `${name} dey call you to drive together.`,
    seeInvite: 'See',
  },
  profile: {
    title: 'Your profile',
    back: 'Back',
    loading: 'Opening your profile…',
    failed: 'We no fit open your profile. Try again small time.',
    fromLagosLife: 'Your name and picture come from Lagos Life. Change them there or drive with a nickname.',
    ownName: 'The name you drive with in every park.',
    nick: {
      title: 'NICKNAME',
      note: 'Drive with a nickname instead of your name. Nobody else fit take the same one.',
      yours: (n: string) => `Everybody dey see ${n}.`,
      add: 'Add a nickname',
      change: 'Change',
      drop: 'Use my name',
      label: 'Your nickname',
      placeholder: 'e.g. Oga Driver',
      save: 'Save',
      cancel: 'Cancel',
      saved: 'Nickname don set. Your next park go see am.',
      dropped: 'You don go back to your name.',
    },
    colour: 'Your keke colour is given when you enter a park.',
    level: (n: number) => `Level ${n}`,
    xp: (into: string, next: string) => `${into} of ${next} XP to the next level`,
    top: 'Top level. Na you be oga.',
    xpNote: 'Rounds give XP: finishing, winning, stops and right votes. Levels are for show only.',
    totals: { rounds: 'Rounds', wins: 'Wins', stops: 'Stops', rightVotes: 'Right drops' },
    badges: 'Badges',
    badgeDone: 'Done',
    badgeOpen: 'Not yet',
    gives: (name: string) => `Gives: ${name}`,
    looks: 'Your looks',
    looksNote: 'What you use here shows on your keke in your next park.',
    none: 'Nothing of this kind yet. The shop has some.',
    using: 'Done. Everybody go see am for your next park.',
    kindsLabel: 'Kinds of looks',
  },
  people: {
    title: 'Your people',
    back: 'Back',
    loading: 'Finding your people…',
    party: 'Your crew',
    partyCode: 'Crew code',
    partyNote: 'Tell your people this code, or call drivers you played with below.',
    start: 'Start a crew',
    startNote: 'Start a crew, then go to one park together. Everybody enters, or nobody.',
    joinLabel: 'Crew code',
    join: 'Join crew',
    leave: 'Leave crew',
    leader: 'Leader',
    you: 'You',
    count: (n: number, max: number) => `${n} of ${max}`,
    bring: 'Bring my people',
    bringNote: (code: string | null) =>
      code ? `Everybody goes to your park ${code}.` : 'We find one park with room for all of you.',
    waiting: (name: string) => `Wait here. ${name} go carry everybody go one park.`,
    going: (code: string) => `Your crew dey for park ${code}. We dey enter…`,
    recent: 'Drivers you played with',
    recentNone: 'Finish a round and the drivers you played with show here.',
    recentNote: 'Only drivers you finished a round with. Nobody else can find you here.',
    call: 'Call',
    called: 'Called',
    invites: 'Calls for you',
    invite: (name: string) => `${name} dey call you`,
    accept: 'Join',
    dismiss: 'No',
    busy: (name: string) => `${name} still dey for another park. Wait for am, then try again.`,
  },
  challenges: {
    title: 'Today',
    resets: 'New ones at midnight',
    confirm: (naira: string) => `Swap this one for ${naira} park money? You get one swap a day.`,
    yes: 'Swap am',
    no: 'No',
    swapped: 'E don change. Your new one dey there.',
  },
  pass: {
    title: 'Route Pass',
    back: 'Back',
    loading: 'Opening the Route Pass…',
    none: 'No season dey run now. The next one go show here.',
    runs: (day: string) => `Runs until ${day}.`,
    ended: (day: string) => `Ended ${day}. Collect what you earned.`,
    confirm: (naira: string) => `Open the premium track for ${naira} park money? The money comes off your wallet now.`,
    confirmYes: 'Open am',
    confirmNo: 'No',
    opened: 'Premium track open. Collect your rewards.',
    collected: 'Collected.',
    note: 'Rounds give XP: finishing, winning, stops, right votes. Rewards are looks and park money only.',
  },
  shop: {
    title: 'Shop',
    back: 'Back',
    getCoins: 'Get park money',
    getCoinsPitch: "Paid on Lagos Life's own sheet.",
    /** The buy button: the price the store charges, in plain digits. */
    packPrice: (naira: number) => `₦${naira.toLocaleString('en-NG')}`,
    packBuyFor: (amount: string, naira: number) => `Buy ${amount} park money for ₦${naira.toLocaleString('en-NG')}`,
    /** Under each pack's amount, so it never reads as the price Lagos Life charges. */
    packWord: 'park money',
    packNote:
      "Park money is paid for on Lagos Life's own sheet, which shows the price again before you pay. Park money buys looks only: never fare, never a seat, never cash.",
    packsOff: 'Park money packs open inside Lagos Life.',
    /** Our website's packs: paid on the payment company's own page (Stig Labs pay sends them there). */
    getCoinsPitchWebsite: 'Paid on a secure payment page.',
    packNoteWebsite:
      "You pay on the payment company's own page, which shows the price again before you pay. We never see your card or bank details. Park money buys looks only: never fare, never a seat, never cash.",
    /** Back from the payment page. */
    paying: 'We dey check your payment...',
    payPending: 'The payment never confirm yet. Once e land, the park money go enter your wallet.',
    payFailed: 'The payment no go through. Nobody charge you.',
    payRefunded: 'That payment was refunded, so its park money don go.',
    payTryAgain: 'The payment page no answer. Try again small time.',
    added: (n: string) => `+${n} park money. E dey your wallet now.`,
    bought: 'Na your own now. You dey use am.',
    using: 'You dey use am. Everybody go see am for your next park.',
    kinds: {
      load: 'Loads',
      slogan: 'Slogans',
      finish: 'Paint',
      horn: 'Horns',
      owa: 'Owa scenes',
      sticker: 'Stickers',
      power: 'Power skins',
    } as Record<string, string>,
    kindsLabel: 'What to show',
    note: 'Looks and sounds only. Nothing here changes play. Your keke paint comes from your seat in the park; every look goes on any paint.',
    debt: (n: string) =>
      `A refunded pack left ${n} park money to pay back. The next park money you earn or buy goes there first.`,
    loading: 'Opening the shop…',
  },
  sound: {
    on: 'Sound on',
    off: 'Sound off',
    volume: 'Volume',
    volumes: { low: 'Low', normal: 'Normal', loud: 'Loud' } as Record<string, string>,
  },
  /** Game types by id (older rounds recorded under a rule set that is gone show as the Park). */
  presets: {
    park: 'The Park',
    race: 'Race',
    powers: 'Power-ups',
    hide: 'Hide and seek',
    stakes4p: 'Stake table',
    stakesRace: 'Race stake table',
    beginner: 'The Park',
    classic: 'The Park',
    fast: 'The Park',
    advanced: 'The Park',
  } as Record<string, string>,
  // the Play menu (the game modes spec): who you play with, then what you play
  play: {
    title: 'Play',
    ask: 'Wetin you go play?',
    held: (code: string) => `Your seat still dey for park ${code}.`,
    back: 'Back to park',
    // a game type's sheet: strangers, or your own park for your people
    quick: 'Quick play',
    quickNote: 'Jump into a park wey dey start now.',
    open: 'Open a park',
    openNote: 'Open your own park and call your people with the code.',
    parks: 'Parks wey dey wait',
    none: 'No park like this one dey wait. Open one or tap Quick play.',
    close: 'Close',
    // Play alone (solo in the code): you on your own, with other drivers who play alone; never with
    // your people (that is Open a park and the code). It never counts for rankings
    solo: 'Play alone',
    soloNote: 'Na you alone with other drivers. To play with your people, open a park and share the code.',
    soloWhat: 'Which game?',
    /** The one line on top of Play alone: who you play with, and that it pays. */
    soloLead: 'Na you and solo drivers. The rounds dey pay park money.',
    /** Race has no sides: said in place of the side pick. */
    soloRace: 'No side for Race. First to finish carry am.',
    /** Race's board in place of the side pick: the player's own keke. */
    soloRaceTitle: 'Your keke',
    soloLoad: 'The game no load. Check your network, then try again.',
    /** Play alone with no network: the on-phone round, which pays nothing. */
    soloOffline: 'Network no dey, so this round go play for your phone. E no go pay.',
    /** Over the road from the start of every 3D round until the street draws (game/road.ts). */
    street: 'The street dey load. Small time.',
    /** The street's loading bar's label. */
    streetBar: 'Street',
    /** What each game is about, in a line or two: the top of its sheet (and of Play alone, for the game picked). */
    about: {
      park: 'Run your route and carry passengers for the motor park. One driver na VIO in disguise: e go arrest you if e catch you alone. Talk, point, drop am.',
      powers:
        'The Park with power-ups on the road: pick a boost, a wiper or a fake breakdown, use am, waka pass. The VIO still dey among una.',
      hide: 'Everybody know the VIO, and e dey come. Hide, do your stops, last till the clock finish.',
      race: 'Three laps of Third Mainland. Watch your fuel, dodge the potholes and the agberos, first over the line carry am.',
      stake: 'Four drivers put fare down. The best driver for the round carry the pot.',
    } as Record<string, string>,
    soloStart: 'Start',
    sides: { driver: 'Driver', agbero: 'VIO', surprise: 'Surprise me' },
    stake: 'Stake table',
    sessionWeather: (naira: string) => `Weather for the whole park (${naira} park money)`,
    drawn: 'Random',
  },
  // area boys on the road and the purse (the game modes spec)
  boys: {
    lines: {
      settle: 'Oga, settle us.',
      wetin: 'Wetin you carry?',
      pass: 'Your own don do, pass.',
      here: 'We dey here o.',
    } as Record<string, string>,
    settled: (coins: number) => `Levy paid: ${parkNaira(coins)}`,
    /** The levy prompt: an agbero on your keke without a paid ticket. */
    pay: (coins: number) => `Pay ${parkNaira(coins)}`,
    pass: 'No pay',
    banked: (coins: number) => `${parkNaira(coins)} park money banked`,
    bankedLine: 'The safe part: agberos no fit touch am. You keep am even if your side lose.',
    sent: 'The boys dey follow that keke',
    send: 'Send the boys',
    purse: 'Purse',
    map: 'The map: you and the area boys’ posts',
    bankedShort: (n: string) => `${n} safe`,
  },
  // the people of the street (game/people): what passers-by, passengers and the police say as you pass
  street: {
    lines: {
      market: ['Fine tomato, come buy.', 'Customer, I go dash you.', 'Na last price be that.'],
      water: ['Pure water, cold one.', 'Buy water, e dey cold.'],
      okada: ['Where you dey go?', 'Oga, make I carry you.'],
      student: ['We don close from school.', 'I never do my assignment o.'],
      banker: ['Network don fail again.', 'I don late for office.'],
      pastor: ['God go do am.', 'Come to church on Sunday.'],
      maishai: ['Tea dey, bread dey.', 'Come chop, e dey hot.'],
      conductor: ['Oshodi, Oshodi, enter with your change.', 'Mile 2, e remain one.', 'CMS, CMS, wole.'],
      aunty: ['This sun no be here o.', 'My pikin, hold my hand.'],
      baba: ['Wetin dey happen for this road?', 'Una no dey hear word.'],
      man: ['Abeg, wetin be the time?', 'Traffic don hold everywhere.'],
      lady: ['Make I quick buy pepper.', 'Abeg shift small.'],
      police: ['Oya, park there.', 'Show me your particulars.'],
      rider: ['Keke, stop here.', 'I dey go Oshodi.', 'Abeg, carry me.'],
    } as Record<string, readonly string[]>,
    /** An officer after somebody. */
    chase: 'Stop there o.',
  },
  /** The time of day: every round starts in daylight, then evening comes, then night. */
  daylight: {
    came: { evening: 'Evening don reach', night: 'Night don fall' } as Record<string, string>,
  },
  weather: {
    // the lobby's weather line and the buy sheet (looks only: the same weather for everyone)
    drawn: 'The weather is drawn each round.',
    next: (name: string, by: string) => `${by} called ${name} for the next round.`,
    session: (name: string) => `${name} every round in this park.`,
    buy: 'Buy the weather',
    note: 'The first buy before the round wins. Nobody pays for a weather already bought.',
    taken: 'Somebody don buy the weather already.',
    bought: 'Done. The next round go get am.',
    coming: (line: string) => line,
  },
  lobby: {
    /** A cash heat's lobby: it starts on its clock, stand-ins take the empty seats then. */
    heatIn: 'HEAT STARTS IN',
    heatLine: 'Stand-ins take the empty seats at the start.',
    title: 'Park code',
    tellThem: 'Tell your people this code. They enter it on their phone.',
    waiting: (n: number, min: number) =>
      n >= min ? `${n} drivers in the park. Ready to roll.` : `${n} in the park. Need ${min - n} more to start.`,
    start: 'Start the route',
    onlyHost: 'The host starts the route.',
    countdown: (s: number) => `Moving in ${s}`,
    // Quick Play: the park looks for people a few seconds, then solo drivers fill it and the round starts
    quickFind: 'Dey find drivers…',
    quickJoin: 'Solo drivers dey join',
    // the countdown card over the lobby
    engines: 'Engines on',
    go: 'Oya',
    countdownNote: 'Your role card dey come. Hide your screen.',
    leave: 'Leave park',
    openPark: 'Open park',
    openNote: 'Anyone fit find am for Open parks.',
    privatePark: 'Private park',
    privateNote: 'Only people wey get the code fit enter.',
    host: 'Host',
    away: 'Network don cut',
    reopens: (s: number) => `Park reopens in ${s}`,
    theme: { day: 'Clear sky', night: 'Night road', rain: 'Rainy season', harmattan: 'Harmattan haze' } as Record<
      string,
      string
    >,
    ticket: (n: number) => `Use a role ticket for the next round (${n} left)`,
    ticketNote: 'Only you can see this. A ticket makes you twice as likely to be VIO and is used only if you get it.',
    // the host's menu on a driver in the roster (BE-10)
    options: (name: string) => `Options for ${name}`,
    kick: 'Remove from park',
    kickAsk: (name: string) => `Remove ${name} from this park?`,
    kickNote: 'They no go fit enter this park again.',
    kickYes: 'Remove',
    kickNo: 'Keep them',
    // the ready switch (FE-07): each driver says they are set; the host still starts the route
    readyBadge: 'Ready',
    imReady: 'I dey ready',
    drivers: 'Drivers',
    readyCount: (n: number, of: number) => `${n} of ${of} ready`,
    // the park's talk in the lobby (BE-13): folded into one line over the dock until opened
    talk: 'Park talk',
    talkEmpty: 'Nobody don talk yet. Greet the park.',
    talkLast: (name: string, text: string) => `${name}: ${text}`,
    talkNew: (n: number) => (n === 1 ? '1 new line' : `${n} new lines`),
    talkClose: 'Close',
  },
  // the park's rules on top of its preset (FE-05): the host changes them in the lobby
  install: {
    text: 'Add Wahala to your home screen.',
    add: 'Add',
    later: 'Not now',
    /** iPhone and iPad Safari: no install offer, so the bar says where Safari keeps it. */
    ios: 'Add Wahala to your home screen: tap Share, then Add to Home Screen.',
    ok: 'OK',
    /** The Menu's way in, and the sheet it opens (also from the website's Get the app link). */
    get: 'Get the app',
    getNote: 'Wahala for your home screen, like any app.',
    band: 'Get the app',
    lead: 'Put Wahala for your home screen. E go open like any other app: no Play Store, no App Store.',
    go: 'Install Wahala',
    done: 'Wahala don land for your home screen. Open am from there next time.',
    have: 'Wahala don already dey your home screen. Open am from there.',
    steps: {
      ios: [
        'Tap Share: the square with the arrow, for the bottom.',
        'Scroll down and tap Add to Home Screen.',
        'Tap Add.',
      ],
      iosOther: [
        'Tap Share for your browser.',
        'Tap Add to Home Screen.',
        'If you no see am, open this page for Safari and do am there.',
      ],
      inapp: [
        'This page dey open inside another app.',
        'Tap the menu (⋮ or ...) and choose Open in Chrome or Open in browser.',
        'For the browser, open Menu again and tap Get the app.',
      ],
      manual: ['Tap your browser menu (⋮) for the top.', 'Tap Install app or Add to Home screen.', 'Tap Install.'],
    },
  },
  /** Web push (FE-51): the ask after a first round, and the Settings rows. Standalone players only. */
  push: {
    ask: 'Make we tell you when your people call you?',
    yes: 'Yes',
    later: 'Not now',
    title: 'Notifications',
    turnOn: 'Turn on',
    turnOff: 'Turn off',
    on: 'On',
    off: 'Off',
    kinds: {
      calls: 'Calls',
      park: 'Park ready',
      today: 'Today',
      rankings: 'Rankings',
      news: 'News',
    } as Record<string, string>,
    notes: {
      calls: 'When your people call you',
      park: 'When your park don full',
      today: 'New challenges, once a day',
      rankings: 'Your week, once a week',
      news: 'News from the park office',
    } as Record<string, string>,
    blocked: 'Your browser don block notifications for Wahala. Allow am for the site settings.',
    iphone: 'For iPhone, add Wahala to your home screen first. Then open am from the icon.',
    denied: 'No wahala. You fit turn them on later for Settings.',
    failed: 'Notifications no work now. Try again small time.',
    test: 'Send me a test',
    testSent: 'Test don go. E go show for this phone soon.',
    help: 'Not getting notifications?',
    helpItems: [
      'Check say your browser allow notifications for Wahala: site settings, Notifications, Allow.',
      'iPhone: add Wahala to your home screen, then open am from the icon.',
      'Focus or Do Not Disturb for your phone fit hold them back.',
    ],
  },
  invite: {
    open: 'Invite people',
    close: 'Close',
    title: 'Invite people',
    line: 'Come drive with me in Wahala.',
    scan: 'Scan or type the code',
    howFramed: 'Screenshot this card and post it anywhere, or read the code out.',
    howOpen: 'Send the link, or show this card.',
    share: 'Share',
    shareText: (code: string) => `Join my Wahala park. Code: ${code}`,
    copied: 'Link copied.',
    qrLabel: (code: string) => `QR code for park ${code}`,
    /** The community chat: the code goes to a room or a message, with a Join under it. */
    chat: 'Share in chat',
  },
  perks: {
    title: 'Make the park your own',
    note: 'Free parks only. If the park never starts a round, the money comes back.',
    name: (naira: string) => `Park name (${naira} park money)`,
    namePlaceholder: 'e.g. Oshodi Boys',
    theme: (naira: string) => `Weather for every round (${naira} park money)`,
    plain: 'Random',
    total: (naira: string) => `Opening this park costs ${naira} park money.`,
  },
  // promos from the park office: a coupon code in the shop, and the first-play welcome gift
  promo: {
    title: 'Have a code?',
    label: 'Promo code',
    placeholder: 'e.g. OSHODI2K',
    redeem: 'Use code',
    redeeming: 'Checking…',
    worked: (coins: string) => `Code don work: ${coins} park money don land.`,
    pass: 'Route Pass premium don open.',
    welcome: (coins: string) => `Welcome gift: ${coins} park money to start you off.`,
    welcomePass: 'Route Pass premium don open for you too.',
  },
  tickets: {
    title: 'Role ticket',
    line: (naira: string) =>
      `${naira} park money each. In a free park, an armed ticket makes you twice as likely to be VIO. It is used only if you get the role and never in a stake park.`,
    have: (n: number) => (n === 1 ? 'You get 1 ticket.' : `You get ${n} tickets.`),
    buy: 'Buy one',
    bought: 'Ticket dey your pocket. Arm am for the lobby.',
  },
  role: {
    crew: 'You be driver',
    crewLine: 'Finish your stops. Find the VIO.',
    agbero: 'You be VIO',
    agberoLine: 'Arrest drivers when nobody dey look. No let the park catch you.',
    partners: (names: string) => `Your VIO people: ${names}`,
    /** Parks with arrests: the impostor is a corrupt VIO in a keke. */
    vio: 'You be VIO',
    vioLine: 'Arrest drivers when nobody dey look. No let the park catch you.',
    vioPartners: (names: string) => `Your VIO people: ${names}`,
  },
  /** How the round ends, under the role card (WinStory): a few words per scene, the stamp says who wins. */
  winStory: {
    title: 'How e go end',
    stamps: { crew: 'Drivers win', agbero: 'VIO win' },
    /** The park's own numbers: stops the drivers share, VIOs dealt (the same for everyone). */
    park: {
      route: (stops: number) => (stops > 0 ? `All ${stops} stops don finish` : 'Every stop don finish'),
      drop: (vios: number) =>
        vios <= 1 ? 'The VIO don drop' : vios === 2 ? 'Both VIOs don drop' : `All ${vios} VIOs don drop`,
      parity: (vios: number) =>
        vios <= 1 ? '1 VIO, 1 free driver remain' : `${vios} VIOs, ${vios} free drivers remain`,
    },
    hide: { clock: 'Hide till the clock finish', caught: 'VIO catch every keke' },
  },
  hud: {
    route: 'Route',
    stop: 'Do stop',
    report: 'Report',
    horn: 'Horn',
    /** The horn once you have used every call the park gives you. */
    hornNone: 'No horn',
    hornsLeft: (n: number) => (n === 1 ? '1 horn call left' : `${n} horn calls left`),
    /** Hide and seek's VIO: he catches the drivers he finds (impounds their kekes). */
    knock: 'Catch',
    /** A VIO's knock in parks with arrests. */
    arrest: 'Arrest',
    stops: 'Your stops',
    map: 'Stops',
    barAtMeetings: 'The route moves when the park meets.',
    stopsCount: (done: number, total: number) => `${done}/${total}`,
    closeStops: 'Close your stops',
    /** Network don go: the board can't load your stops until the mast is reset. */
    noSignal: 'No signal',
    noSignalNote: 'Reset the mast to see your stops again.',
    departed: (name: string) => `${name} don comot`,
    holding: (label: string) => label,
    ghost: 'Your stops still count. Finish them as spirit.',
    ghostTitle: 'Spirit keke',
    spiritTalk: 'Talk with the spirits',
    spiritLast: (name: string, text: string) => `${name}: ${text}`,
    spiritClose: 'Close spirit talk',
    ghostDropped: 'Dropped. Spirit keke',
    cooldown: (s: number) => `${s}s`,
  },
  /** Wahala (sabotage, FE-21): screen words from the design system's Mechanics section. */
  wahala: {
    disc: 'Wahala',
    fix: 'Fix',
    pick: 'Which wahala?',
    cancel: 'No wahala',
    kinds: {
      nepa: 'NEPA don take light',
      network: 'Network don go',
      goslow: 'Go-slow on Third Mainland',
      fuel: 'Fuel scarcity',
    } as Record<string, string>,
    notes: {
      nepa: 'Drivers see only a headlamp beam.',
      network: 'The route bar freezes and drivers lose sight of their stops.',
      goslow: 'Two drivers must clear both ends, or you win.',
      fuel: 'Two kekes must queue together, or you win.',
    } as Record<string, string>,
    fixed: 'Wahala don clear',
    parts: (fixed: number, need: number) => `${fixed} of ${need} done. Make somebody go the other side.`,
  },
  /** Play settings (FE-25), per phone. */
  prefs: {
    side: 'Stick side',
    sides: { right: 'Right', left: 'Left' } as Record<string, string>,
    stick: 'Stick size',
    sizes: { s: 'Small', m: 'Medium', l: 'Big' } as Record<string, string>,
    text: 'Text size',
    texts: { s: 'Small', m: 'Normal', l: 'Big' } as Record<string, string>,
    /** The road's effects: calm drops the bursts, the shake and the smoke. */
    effects: 'Road effects',
    effectsKinds: { full: 'Full', calm: 'Calm' } as Record<string, string>,
    /** The park behind a round: crowd, distant horns, music from a shop, hawkers. */
    parkSound: 'Park sound',
    parkSounds: { on: 'On', off: 'Off' } as Record<string, string>,
  },
  /** The Lagos Life leaderboard (PF-10): rounds won. Only a number, never a prize. */
  history: {
    title: 'Your rounds',
    back: 'Back',
    loading: 'Loading your rounds…',
    failed: 'Your rounds no load. Try again small time.',
    none: 'No round dey yet. Play one, e go show here.',
    note: 'Your last 50 rounds. Park money is what each round paid you; fare is Lagos Life game money from stake tables.',
    totals: { rounds: 'Rounds', wins: 'Won', coins: 'Park money earned', fare: 'Fare won' } as Record<string, string>,
    role: { crew: 'Driver', impostor: 'VIO' } as Record<string, string>,
    won: 'Won',
    lost: 'Lost',
    left: 'Left early',
    coins: (n: string) => `+${n} park money`,
    noCoins: 'No park money',
    carried: (n: string) => `You carry ${n} fare`,
    stakeLost: (n: string) => `${n} fare in the pot`,
    fareBack: 'Fare came back',
    settling: 'Pot dey settle',
    park: (code: string) => `Park ${code}`,
  },
  board: {
    title: 'Most wins',
    period: 'All time',
    unit: 'Rounds won',
    loading: 'Loading the board…',
    failed: 'The board no load. Try again small time.',
  },
  /** Global rankings (the rankings spec): your rank and park money on top, the board, your games. Points, never money. */
  rankings: {
    title: 'Rankings',
    you: 'You',
    youTag: 'you',
    yourRank: 'Your rank',
    /** A rank already written "1,204" -> "#1,204". */
    hash: (n: string) => `#${n}`,
    points: (n: string) => `${n} points`,
    top: (p: number) => `Top ${p}%`,
    next: (pts: string, tier: string) => `${pts} more points to ${tier}`,
    topTier: 'You don reach the top. Hold am tight.',
    earned: 'Park money earned',
    fromRounds: (n: string) => (n === '1' ? 'from 1 round' : `from ${n} rounds`),
    fareWon: 'Fare won',
    fareWhere: 'at stake tables',
    loadingYou: 'Checking your rank…',
    failedYou: 'Your rank no load. Check your network.',
    retry: 'Try again',
    /** A player with no rounds yet. */
    fresh: {
      title: 'Your keke never touch road',
      text: 'Finish one full round and your rank go show here, with the park money you earn. Wetin you dey wait for?',
      go: 'Start driving',
    },
    unranked: 'No rank yet. Finish one full round and you go enter the board.',
    /** The board and its filter. */
    board: 'Top drivers',
    filter: 'Which board',
    games: {
      all: 'All',
      park: 'The Park',
      race: 'Race',
      hide: 'Hide and seek',
      powers: 'Power-ups',
    } as Record<string, string>,
    /** The filter's short names (five fit 375px on one line). */
    short: { all: 'All', park: 'Park', race: 'Race', hide: 'Hide', powers: 'Powers' } as Record<string, string>,
    allGames: 'All games',
    unit: 'Points',
    more: 'See the top 50',
    less: 'Show only the top 10',
    empty: 'Nobody dey this board yet. Na your chance be this.',
    loading: 'Loading the board…',
    failed: 'The board no load. Try again small time.',
    /** Your games: one line each. */
    yourGames: 'Your games',
    noGameRank: 'No rank yet',
    numbers: 'See all your numbers',
    /** "38 rounds, 13 won, 96 stops" for one game type: only what that game counts. */
    count: {
      rounds: (n: string) => `${n} rounds`,
      wins: (n: string) => `${n} won`,
      stops: (n: string) => `${n} stops`,
      knocks: (n: string) => `${n} arrests`,
      reports: (n: string) => `${n} reports`,
      rightVotes: (n: string) => `${n} right points`,
      raceFinishes: (n: string) => `${n} finishes`,
      racePodiums: (n: string) => `${n} podiums`,
      raceBeaten: (n: string) => `${n} kekes passed`,
    } as Record<string, (n: string) => string>,
    fareLine: (staked: string, won: string, tables: string) =>
      `Fare: ${staked} put down, ${won} won over ${tables} stake tables.`,
    fareNote: 'Fare na Lagos Life game money. E count once Lagos Life settle the table. E no dey mix with park money.',
    prizeSoon: 'Cash prizes for website drivers dey come. Your wins go count when e land.',
    how: 'Points come from rounds you finish, wins (more for a full park), stops, arrests, reports, right points and race finishes. Park money add small, but money alone no fit buy the top.',
    solo: (n: string) => `${n} ${n === '1' ? 'round' : 'rounds'} wey you play alone no count for rankings.`,
    note: 'Rounds wey you play alone and rounds under two minutes no count for rankings.',
    /** The quiet link to the board of rounds won. */
    other: { world: 'See the Lagos Life board', ours: 'See the most wins board' } as Record<string, string>,
    back: 'Back to rankings',
    tiers: {
      fresh: 'Fresh Hand',
      conductor: 'Conductor',
      driver: 'Driver',
      correct: 'Correct Driver',
      oga: 'Oga Driver',
      boss: 'Garage Boss',
      chairman: 'Park Chairman',
      top: 'Oga at the Top',
    } as Record<string, string>,
    /** The chip on the results board after a round: "+45 points · #1,204 → #1,150". */
    chip: (pts: string, from: string | null, to: string) =>
      from === null ? `+${pts} points · #${to}` : `+${pts} points · #${from} → #${to}`,
    chipTag: 'Rank',
    chipLabel: 'Your rankings',
  },
  /** Shortcuts under the bridge (vents, GP-13): VIOs only. */
  shortcut: {
    disc: 'Shortcut',
    cross: 'Cross',
    out: 'Comot',
    inside: 'You dey under the bridge. Nobody fit see you.',
  },
  /** Stop mini-games (FE-16a, FE-16b). */
  games: {
    wires: { title: 'Fix the wiper', help: 'Tap a wire, then its socket: same colour, same number.' },
    swipe: {
      title: 'Pay the toll',
      help: 'Drag the card across the reader in one steady swipe.',
      fast: 'Too fast. Swipe am again, small small.',
      slow: 'Too slow. One clean swipe.',
      short: 'Carry the card all the way across.',
      card: 'Toll card',
      cardLabel: 'TOLL CARD',
    },
    code: {
      title: 'Collect your change',
      help: 'Punch in the receipt number.',
      receipt: 'Receipt no.',
      clear: 'Clear',
    },
    fuel: {
      title: 'Fuel up',
      help: 'Hold the nozzle. Let go when the tank is inside the yellow band.',
      hold: 'Hold to pump',
      tank: 'Fuel tank',
      under: 'Small more. Fill am to the band.',
      over: 'E don pass. Try again.',
    },
    pump: {
      title: 'Patch the tyre',
      help: 'Pump the tyre to the mark, then say done.',
      gauge: 'Tyre pressure',
      pump: 'Pump',
      done: 'Done',
      over: 'Too much air. Let am out and pump again.',
      count: (n: number, of: number) => `${n} of ${of}`,
    },
    clamps: {
      title: 'Jump-start the battery',
      help: 'Red clamp on plus first, then black on minus.',
      battery: 'Battery',
      red: 'Red clamp next',
      black: 'Black clamp next',
      post: (sign: string, clamp: string) => `${sign === '+' ? 'Plus' : 'Minus'} terminal: put the ${clamp} clamp here`,
    },
    load: {
      title: 'Load full',
      help: 'Wait at the stop while passengers climb in. No drive off.',
      seats: 'Seats filled',
      seat: (n: number, full: boolean) => `Seat ${n}: ${full ? 'taken' : 'empty'}`,
      full: 'Keke don full. Oya, move.',
    },
    inspect: {
      title: 'Check the oil',
      help: 'One sample is bad. Tap the dark one.',
      sample: (n: number) => `Oil sample ${n}`,
    },
    spot: {
      title: 'Find your passenger',
      help: (look: string) => `Your passenger is wearing ${look}. Tap them.`,
      looks: ['a head-tie', 'a face cap', 'glasses', 'a school bag'],
      person: (n: number) => `Passenger ${n}`,
    },
    sort: {
      title: 'Count the fare',
      help: 'Tap the notes from the smallest to the biggest.',
      note: (naira: number) => `₦${naira.toLocaleString('en-NG')} note`,
      wrong: 'No be that one. Start again from the smallest.',
      counted: (n: number, of: number) => `${n} of ${of} counted`,
    },
    wash: {
      title: 'Wash the keke',
      help: 'Scrub every muddy panel. Anybody near fit see you washing.',
      panel: (n: number, muddy: boolean) => `Panel ${n}: ${muddy ? 'muddy' : 'clean'}`,
    },
    waiting: (s: number) => `Ready in ${s} s`,
    again: 'No be am. Try again.',
    leave: 'Leave am',
    wire: (n: number) => `Wire ${n}`,
    socket: (n: number) => `Socket for wire ${n}`,
  } as {
    wires: { title: string; help: string };
    swipe: { title: string; help: string; fast: string; slow: string; short: string; card: string; cardLabel: string };
    code: { title: string; help: string; receipt: string; clear: string };
    fuel: { title: string; help: string; hold: string; tank: string; under: string; over: string };
    pump: {
      title: string;
      help: string;
      gauge: string;
      pump: string;
      done: string;
      over: string;
      count: (n: number, of: number) => string;
    };
    clamps: {
      title: string;
      help: string;
      battery: string;
      red: string;
      black: string;
      post: (sign: string, clamp: string) => string;
    };
    load: { title: string; help: string; seats: string; seat: (n: number, full: boolean) => string; full: string };
    inspect: { title: string; help: string; sample: (n: number) => string };
    spot: { title: string; help: (look: string) => string; looks: string[]; person: (n: number) => string };
    sort: {
      title: string;
      help: string;
      note: (naira: number) => string;
      wrong: string;
      counted: (n: number, of: number) => string;
    };
    wash: { title: string; help: string; panel: (n: number, muddy: boolean) => string };
    waiting: (s: number) => string;
    again: string;
    leave: string;
    wire: (n: number) => string;
    socket: (n: number) => string;
  } & Record<string, unknown>,
  /** Tell the park office about a player (BE-17). Never "report": that word is for breakdowns. */
  office: {
    open: 'Tell the park office',
    title: 'Park office',
    who: 'Who do you want to tell us about?',
    about: (name: string) => `About ${name}. What happened?`,
    why: 'What happened',
    reasons: {
      cheating: 'Cheating',
      abuse: 'Rude or threatening',
      name: 'Bad name',
      other: 'Something else',
    } as Record<string, string>,
    noteLabel: 'Tell us more (optional)',
    mute: (name: string) => `Hide ${name}'s chat on this phone`,
    send: 'Send to the park office',
    thanks: 'Thank you. The park office will look at it.',
    failed: 'We no fit send am. Try again small time.',
    close: 'Close',
  },
  /**
   * Arrests (parks with VIOs): Bribe or Bail, the Task Force Yard and Cut the Padlock. The engine's
   * kill is the screen's arrest; a clamped keke is an impounded keke.
   */
  arrest: {
    board: 'Bribe or Bail',
    note: 'VIO don catch you. Tap when the needle enter the sweet spot.',
    zones: { lawyer: 'Lawyer', bribe: 'Sweet spot', yard: 'Wahala' } as Record<string, string>,
    tap: 'Tap',
    tapped: 'You don tap. Wait small.',
    left: (s: number) => `${s}s`,
    officer: (name: string) => `You dey arrest ${name}. No move.`,
    /** How Bribe or Bail went, in one line. */
    outcome: (outcome: string, undone: number) =>
      outcome === 'lawyer'
        ? 'Lawyer don free you. Carry go.'
        : outcome === 'bribe'
          ? undone === 1
            ? 'Bribe paid. 1 stop don cancel.'
            : `Bribe paid. ${undone} stops don cancel.`
          : outcome === 'yard'
            ? 'Dem clamp your keke. Yard.'
            : 'The park don gather. Carry go.',
    officerTitle: {
      lawyer: 'Lawyer collect am',
      bribe: 'E pay bribe',
      yard: 'Keke don clamp',
      released: 'The park don gather',
    } as Record<string, string>,
    yard: 'Task Force Yard',
    yardLine: 'Yard. Wait for padlock cut.',
    cut: 'Cut padlock',
    cutStage: (stage: number, of: number) => `Padlock ${stage}/${of}`,
    freed: (n: number) => (n === 1 ? 'Padlock cut. 1 driver don comot' : `Padlock cut. ${n} drivers don comot`),
    impounded: 'Impounded keke',
  },
  /** The agberos' levy: a ticket keeps them off; without one, one hangs off your keke till you pay. */
  levy: {
    beg: 'Beg am',
    ticket: 'Ticket',
    ticketLabel: 'You get paid ticket. Agberos go leave you.',
    latched: 'Agbero don hold you',
    pinned: 'Agbero pin you. Wait am out.',
    razaq: 'Razaq',
    razaqLines: ['Na me be Razaq. Ticket or levy.', 'Under-bridge na my office.'],
    onLines: ['Oga, drop levy.', 'You no get ticket? Pay.'],
    gone: 'The agbero don comot',
  },
  /** In-game callouts: news on the road nobody should miss. */
  callout: {
    wahala: 'Wahala!',
    halfFixed: 'One side don clear',
    picked: (name: string) => `${name} dey your hand`,
    powerOn: (name: string) => `${name} on`,
    fake: 'Fake breakdown. Na trick.',
    stopDone: 'Stop done',
    knocked: 'VIO don catch you. Your keke don impound',
  },
  /** Passengers: people wait at the stops, you stop to load them and drop them where they are going. */
  ride: {
    pickUp: 'Pick up',
    dropOff: 'Drop off',
    /** The purse on the road: what fares paid you this round (park money), or drops in a stake park. */
    coins: (n: number) => `${parkNaira(n)} park money`,
    /** Beside the purse's amount: our money, never fare. */
    purseWord: 'park money',
    drops: (n: number) => (n === 1 ? '1 drop' : `${n.toLocaleString('en-NG')} drops`),
    seats: (taken: number, of: number) => `${taken} of ${of} seats taken`,
    /** Little pops beside the purse. */
    paid: (n: number) => `+${parkNaira(n)} park money`,
    dropped: (n: number) => (n === 1 ? '+1 drop' : `+${n} drops`),
    boarded: (n: number) => (n === 1 ? '+1 passenger' : `+${n} passengers`),
    lost: '-1 passenger',
    stopDone: '+1 stop',
    /** The Stops board: who rides with you and where they get off. */
    board: 'Passengers',
    going: (place: string, fare: number) => `For ${place}. Pays ${parkNaira(fare)} park money.`,
    goingDrops: (place: string) => `For ${place}.`,
    empty: 'Nobody dey your keke yet. People dey wait for the stops.',
    /** Shown on the disc while the keke still rolls. */
    slowDown: 'Stop first',
  },
  /** Kekes are solid: a crash stops both dead and shakes them for a moment. */
  crash: {
    bang: 'Gbam',
    line: 'Your keke don jam another one.',
  },
  /** Hide and seek (GP-16b): the VIO is known and comes looking; drivers hide till the clock finishes. */
  hide: {
    coming: 'VIO dey come',
    comingLine: (s: number) => `Go hide. The VIO go move in ${s}s.`,
    waitLine: (s: number) => `Wait. You go move in ${s}s.`,
    /** Before the clock's digits, which the countdown loop writes in place. */
    clockLead: 'Hold on',
    clockLine: 'Every stop you finish cuts the clock.',
    agberoLine: 'Find the drivers. Every keke you catch, you impound.',
    final: 'Final stretch',
    finalLine: 'The VIO fit see far and move fast now.',
    finalAgbero: 'You fit see far and move fast now.',
    escape: (n: number) => `Hide (${n})`,
    inside: (s: number) => `The shortcut go push you out in ${s}s.`,
    roleCrew: (name: string) => `Na ${name} be the VIO. Hide, do your stops, last till the clock finish.`,
    roleAgbero: 'Everybody know say na you. Catch them all before the clock finish.',
  },
  /** Power-up parks (FE-37): powers are picked up on the road, never bought. */
  powers: {
    names: { boost: 'Speed boost', wiper: 'Wiper', decoy: 'Fake breakdown' } as Record<string, string>,
    disc: { boost: 'Boost', wiper: 'Wiper', decoy: 'Fake' } as Record<string, string>,
    fake: 'Na fake breakdown. Nobody arrest anybody.',
    pickup: 'Power',
  },
  /** Special roles (GP-17): engine words are the keys; the screen says these. */
  specials: {
    name: {
      scientist: 'Mechanic',
      engineer: 'Conductor',
      tracker: 'Spotter',
      noisemaker: 'Hailer',
      guardian: 'Prayer warrior',
      shapeshifter: 'Copy-copy',
      phantom: 'Shadow',
    } as Record<string, string>,
    /** One line on the role card: what the role does. */
    what: {
      scientist: 'Open the engine board make you see which keke still dey run.',
      engineer: 'You fit ride the shortcuts like VIO, a few times. No arrest.',
      tracker: 'Mark one keke wey you see. You go follow am for map small.',
      noisemaker: 'If VIO arrest you, the whole park go hear where e happen.',
      guardian: 'When you turn spirit, cover one driver near you from arrest for a while.',
      shapeshifter: 'Wear another keke colour, plate and name for a while.',
      phantom: 'Vanish from the drivers small. You no fit arrest while you dey hide.',
    } as Record<string, string>,
    /** The disc label: the role's button. */
    disc: {
      scientist: 'Board',
      tracker: 'Mark',
      guardian: 'Cover',
      shapeshifter: 'Copy',
      phantom: 'Hide',
    } as Record<string, string>,
    /** The shadow's disc while hidden: step back into sight. */
    unhide: 'Show face',
    /** The role card's extra line. */
    card: (name: string) => `Your role: ${name}`,
    /** Callouts when your ability goes on or off. */
    on: {
      scientist: 'Engine board open',
      tracker: 'Keke marked',
      guardian: 'Driver covered',
      shapeshifter: 'You don copy am',
      phantom: 'You don hide',
    } as Record<string, string>,
    off: 'You don show face',
    /** The conductor's shortcut disc, with rides left. */
    rides: (n: number) => (n === 1 ? 'Shortcut (1 ride)' : `Shortcut (${n} rides)`),
    noRides: 'No rides left',
    /** The mechanic's engine board. */
    board: 'Engine board',
    running: 'Running',
    knocked: 'Stopped',
    /** The spotter's mark when the keke cannot be seen. */
    lost: (name: string) => `${name}: out of sight`,
    marked: (name: string) => `Marked: ${name}`,
    /** A hailer was arrested. */
    alarm: 'Hailer. Check the red ring.',
    /** The lobby's special roles section. */
    section: 'Special roles',
    sectionNote: 'Some drivers and VIOs get a role with a button of its own.',
    count: (name: string) => `How many: ${name}`,
    agberoSide: 'VIO role',
  },
  /** The chat limit's countdown on Send (BE-13): a wait, never an error. */
  chat: {
    wait: (s: number) => `Wait ${s}`,
    waitLabel: (s: number) => `Small wait. You fit send again in ${s} ${s === 1 ? 'second' : 'seconds'}.`,
  },
  meeting: {
    called: 'Wahala!',
    reported: (name: string) => `${name} report breakdown`,
    reportedImpounded: (name: string) => `${name} report impounded keke`,
    horned: (name: string) => `${name} blow horn`,
    discussion: 'Talk first. Pointing comes next.',
    /** The band over the room: talk while the park shouts, point while it votes. */
    talkTitle: 'Talk',
    pointTitle: 'Point',
    /** The one Point bar under the tiles. */
    pointAt: (name: string) => `Point at ${name}`,
    pickOne: 'Tap a keke',
    pointSoon: 'Pointing soon',
    stickerChip: 'Stickers',
    quiet: 'Nobody don talk yet. Type, or tap a quick line.',
    voting: 'Who be the VIO? Tap a keke, then Point.',
    votingVio: 'Who be the VIO? Tap a keke, then Point.',
    voted: 'Don point',
    /** A driver locked in the Task Force Yard: no say in the meeting. */
    yard: 'For the yard. No fit point.',
    chatPlaceholder: 'Talk to the park',
    send: 'Send',
    ghostChat: 'Spirit talk (only spirits see am)',
    noChat: 'Chat opens when the park meets.',
    nobody: 'Nobody drop. The park no agree.',
    nobodyName: 'Nobody',
    nobodyStop: 'the park',
    stickers: 'Your stickers',
    pickSticker: 'Pick a sticker, then tap a keke.',
    throwHint: 'Tap a keke to throw am.',
    stickersDone: 'You don throw all your stickers for this meeting.',
    quick: 'Quick talk',
    // the vote reveal under the Owa (FE-19): who pointed where, when the park shows votes
    reveal: 'How the park point',
    skip: 'Carry go',
    votes: (n: number) => (n === 1 ? '1 point' : `${n} points`),
    secret: 'Votes are secret for this park. Na only the count we fit show.',
    noVotes: 'Nobody point at all.',
    // the small menu on a name in the chat
    menuFor: (name: string) => `${name}: report or mute`,
    report: 'Report',
    mute: 'Mute',
    unmute: 'Unmute',
    muted: (name: string) => `You don mute ${name} for this phone`,
  },
  /** Quick-chat phrases (BE-13): the wire carries only the id, the words live here. */
  quick: {
    'not-me': 'No be me o.',
    'saw-it': 'I see am with my own eye.',
    where: 'Where everybody dey?',
    stops: 'I dey do my stops.',
    lie: 'Na lie.',
    skip: 'Make we skip this one.',
    'vote-now': 'Oya, make we point.',
    ready: 'I ready.',
    'good-game': 'Good game.',
  } satisfies Record<QuickChatId, string>,
  results: {
    drivers: 'Drivers carry the day',
    agberos: 'VIOs take the park',
    vios: 'VIOs take the park',
    /** The headline over the board, from this player's side of it. */
    shout: { reach: 'We don reach!', wahala: 'Wahala!', caught: 'Owa!' },
    reason: {
      votes: 'Every VIO don drop.',
      tasks: 'Every stop don finish.',
      parity: 'VIOs don plenty pass the free drivers.',
      sabotage: 'The wahala no clear on time.',
      forfeit: 'All the drivers don comot.',
      caught: 'The VIO don impound every keke.',
      survived: 'The drivers hide till the clock finish.',
    } as Record<string, string>,
    /** The same reasons where the impostor is a VIO. */
    reasonVio: {
      votes: 'Every VIO don drop.',
      parity: 'VIOs don plenty pass the free drivers.',
    } as Record<string, string>,
    arrests: 'Arrests',
    back: 'Back to the park',
    // the round's numbers (FE-22), from the end screen
    stats: 'The round',
    time: 'Time',
    knocks: 'Arrests',
    drops: 'Drops',
    reports: 'Reports',
    rightVotes: 'Right points',
    // Play again keeps the park together: the board says who is coming back
    againNote: 'Play again go keep everybody for this park. The park go open once all of una ready.',
    stakeDone: 'This stake table don play. Open a new one from the menu.',
  },
  /** The tutorial (practice park): played in the page, never counted anywhere. */
  practice: {
    menu: 'How to play',
    menuNote: 'Practice in your own park. Nothing counts.',
    skip: 'Skip tutorial',
    skipShort: 'Skip',
    next: 'I don hear',
    /** The tutor: the park's old plate-01 keke, who talks you through it. */
    tutor: 'Oga Danfo',
    welcome: {
      title: 'Welcome to the motor park',
      body: 'Every keke get stops to run. But one driver for this park na VIO in disguise: e dey arrest drivers, lock them for the Task Force Yard and cause wahala. The agberos for the bus stops just want their levy.',
      driver: 'Drivers win when the route bar full, or when all the VIOs don drop.',
      agbero: 'VIOs win when they reach as many as the free drivers, or when their wahala timer run out.',
      driverLabel: 'Drivers',
      agberoLabel: 'VIOs',
      start: 'Make we start',
      note: 'A practice park to learn the road. Nothing here counts.',
    },
    reveal: {
      title: 'Your role',
      text: 'Every round go tell you your role for secret. Today, you dey drive. Tap Carry go.',
    },
    drive: {
      title: 'Learn to drive',
      text: 'Push the stick with your thumb, or use the arrow keys or WASD. Drive small make we see.',
    },
    stop: {
      title: 'Pick up passengers',
      text: (where: string) => `Drive to ${where} (follow the arrow), stop inside the ring, then hold Do stop.`,
    },
    drop: {
      title: 'Drop your passenger',
      text: (where: string) => `Now carry am go ${where}. Same thing: inside the ring, then Do stop.`,
    },
    bar: {
      title: 'The route bar',
      text: 'Every stop you finish moves the route bar at the top for the whole park. When it full, drivers win.',
    },
    levy: {
      title: 'The agbero levy',
      text: 'An agbero don hang for your keke: you no get paid ticket, so you dey crawl. Tap Pay on his line make e comot. Once you pay, you get ticket for the round.',
    },
    back: {
      title: 'Back to the park',
      text: 'Drive back to the park in the middle. Keep your eyes open.',
    },
    wahala: {
      title: 'Wahala',
      text: 'NEPA don take light. Only a VIO fit cause wahala. Drivers dey fix am for the repair spot; somebody dey go there now.',
    },
    held: {
      title: 'Bribe or Bail',
      text: (vio: string) =>
        `${vio} na VIO and e don arrest you. Watch the needle: tap when e dey the sweet spot (a bribe) or the lawyer (free). Wahala, or no tap, na the yard.`,
    },
    jailed: {
      title: 'Task Force Yard',
      text: (driver: string) =>
        `Dem clamp your keke and lock you for the yard. You no fit do anything now. Wait: ${driver} dey come cut the padlock.`,
    },
    arrestSeen: {
      title: 'You see that?',
      text: (vio: string, driver: string) =>
        `You escape. Now ${vio} don arrest ${driver}. ${driver} no tap on time, so dem clamp the keke and carry am go the yard.`,
    },
    cut: {
      title: 'Cut the padlock',
      text: (driver: string) =>
        `Drive to the Task Force Yard. Stand by the gate and tap Cut padlock, again and again till e open. ${driver} go comot.`,
    },
    horn: {
      title: 'Blow the horn',
      text: (vio: string) =>
        `Now you know say ${vio} na VIO. Drive to the park horn and tap Horn make everybody gather.`,
    },
    discuss: {
      title: 'Park meeting',
      text: 'Everybody gather. Talk wetin you see: tap Shout to type, or throw a sticker. Voting opens soon.',
    },
    vote: {
      title: 'Vote',
      text: 'Tap the keke you suspect, then Point. Or Carry go to skip. The most votes drops that keke. A tie or more skips means nobody drops.',
    },
    voted: {
      title: 'Votes don count',
      text: 'The tally shows how many pointed at each keke and the skips. Then the keke with the most goes. Owa!',
    },
    won: {
      title: 'Drivers win',
      text: 'No VIO left on the road, so the drivers win this round. Next: try the other side.',
      next: 'Try am as VIO',
    },
    agberoReveal: {
      title: 'Now you be VIO',
      text: 'This time your role na VIO. Your stops na fake, nobody else know. Tap Carry go.',
    },
    knockTry: {
      title: 'Make an arrest',
      text: (victim: string) =>
        `Drive close to ${victim} and tap Arrest. E go play Bribe or Bail; if e lose, na the yard. For real round, do am when nobody dey look.`,
    },
    wahalaTry: {
      title: 'Cause wahala',
      text: 'Tap Wahala and pick one. Lights go off, the route bar freezes, or a timer starts: if drivers no fix am on time, VIOs win.',
    },
    shortcut: {
      title: 'Shortcuts',
      text: 'VIOs fit also jump into a shortcut (under the bridge, the gutters) and come out far away. Nobody sees you inside.',
    },
    done: {
      title: 'You don learn the road',
      say: 'Correct. You don drive both sides. Real parks dey wait: shine your eye, trust nobody.',
      driverLabel: 'As driver',
      driver: ['Finish your stops', 'Cut your people out of the yard', 'Vote out the VIO'],
      agberoLabel: 'As VIO',
      agbero: ['Arrest when nobody dey look', 'Cause wahala', 'Lie for the meeting'],
      play: 'Quick play',
      menu: 'Back to menu',
    },
    far: (where: string, tiles: number) => `${where}: ${tiles} tiles`,
    here: (where: string) => `${where}: you don reach`,
  },
  /**
   * The road guide: the park's people talk you through a real round, a line at a time and only
   * when it helps (guide/rules.ts). No buttons, no rules up front.
   */
  guide: {
    who: { danfo: 'Oga Danfo', fada: 'Oga Officer', chairman: 'Chairman' } as Record<string, string>,
    move: 'Push the stick. Make we waka.',
    /** The stick hint (ui/StickHint.tsx): over the stick for a first-timer's first seconds. */
    stickKeke: 'Drag here make your keke move',
    stickFoot: 'Drag here make you waka',
    stop: (where: string) => `Passengers dey wait for ${where}.`,
    stopFirst: (where: string) => `Passengers dey ${where}. Follow the arrow.`,
    hold: 'Tap Do stop. Wait small.',
    bar: 'Each stop dey push the route bar.',
    barMeetings: 'The bar go move when the park meet.',
    horn: 'You see something? Blow the horn.',
    report: 'Wahala! Na breakdown. Tap Report.',
    reportFar: 'Breakdown dey there. Go tap Report.',
    reportImpounded: 'Wahala! VIO clamp this keke. Tap Report.',
    reportAgbero: 'Report am yourself. Nobody go suspect.',
    breakdown: 'Breakdown',
    fix: (kind: string, where: string) => `${kind}. Run go ${where}.`,
    fixHere: 'Tap Fix.',
    discuss: 'Talk wetin you see. Tap Shout.',
    vote: 'Tap the keke you suspect, then Point.',
    secret: 'Psst. Arrest when nobody dey look.',
    secretAgbero: 'Psst. Catch them before the clock finish.',
    // the story beats: what just happened, and what to do with it
    held: 'Tap at the sweet spot.',
    latched: 'Pay the levy make e comot.',
    runner: 'Siren. VIO dey arrest there.',
    hornIt: 'You see am. Blow the horn.',
    clean: 'Clean. Comot before dem see you.',
    cheer: (left: number) => (left === 1 ? 'One stop remain.' : `${left} stops remain.`),
    cheerAll: 'Stops don finish. Well done.',
    paid: 'Passenger don reach. Carry more.',
    knock: 'Nobody dey look. Arrest am.',
    knockAgbero: 'You dey near. Catch am.',
    shortcut: 'Shortcut here. Enter, comot far.',
    wahala: 'Scatter the park. Tap Wahala.',
    lost: (where: string) => `${where} still dey wait. Follow the arrow.`,
    far: (where: string, tiles: number) => `${where}: ${tiles} tiles`,
    here: (where: string) => `${where}: you don reach`,
    dismiss: 'Close',
    /** The line under the route bar: the job right now, always there while you drive. */
    job: {
      label: 'Your job now',
      go: 'Drive there',
      here: 'You don reach',
      doStop: 'Tap Do stop',
      act: 'Drive there like driver',
      actHere: 'Do stop like driver',
      fix: (where: string) => `Run go ${where}`,
      fixHere: 'Tap Fix',
      breakdown: 'Breakdown',
      report: 'Tap Report',
      reportFar: 'Go near am, tap Report',
      agbero: 'VIO',
      arrest: 'Bribe or Bail',
      tapSweet: 'Tap at the sweet spot',
      yard: 'Task Force Yard',
      wait: 'Wait. Your people go cut the padlock',
      pay: 'Pay the levy make e comot',
      pinned: 'E no go collect. Wait am out',
      runnerNear: 'Arrest dey near you. Comot',
      vio: 'VIO',
      knockNow: 'Nobody dey look. Tap Arrest',
      knockNowAgbero: 'You dey near. Tap Catch',
      knockWait: 'Somebody dey look. Wait',
      blend: 'Arrest when nobody dey look',
      blendAgbero: 'Find them before the clock finish',
      waiting: 'Next stop',
      slow: 'Stop the keke first',
      tapDrop: 'Tap Drop off',
      tapPick: 'Tap Pick up',
      drop: (n: number) => (n === 1 ? 'Drop your passenger' : `Drop ${n} passengers`),
      pick: (n: number) => (n === 1 ? '1 passenger dey wait' : `${n} passengers dey wait`),
      tiles: (n: number) => (n === 1 ? '1 tile' : `${n} tiles`),
      opens: (s: number) => (s > 0 ? `E go open in ${s}s` : 'E don open'),
      allDone: 'Stops don finish',
      watch: 'Watch who dey arrest. Blow horn',
    },
  },
  /**
   * Lagos Life's own answers (its SDK's error.code), read before `errors` for a VatarClientError
   * only: our api uses some of the same words (bad_request) for its own refusals.
   */
  vatarErrors: {
    bad_request: 'Lagos Life no gree open that table. Try another fare.',
    limit: 'That fare pass Lagos Life’s limit for today.',
    signed_out: 'Sign in to Lagos Life first, then try again.',
    off: 'Lagos Life don switch this game off for now.',
    not_multiplayer: 'Lagos Life never list this game for tables yet.',
    error: 'Lagos Life get problem. Try again small time.',
  } as Record<string, string>,
  /**
   * Saved website profiles (PF-19): a guest keeps their profile with an email or phone and a PIN,
   * then signs in on any phone. Never shown inside a distributor's frame (Lagos Life and the rest).
   */
  account: {
    offer: {
      band: 'KEEP YOUR PROFILE',
      line: 'Save am with your email and a PIN. Then you fit continue on any phone with your coins, looks and stats.',
      save: 'Save my profile',
      later: 'Not now',
    },
    kinds: { email: 'Email', phone: 'Phone' } as Record<'email' | 'phone', string>,
    kindsLabel: 'Save with',
    /** Sign-in is by email; a profile saved with a phone before still gets in through this link. */
    usePhone: 'I saved my profile with a phone number',
    useEmail: 'Use my email instead',
    contactLabel: { email: 'Your email address', phone: 'Your phone number' } as Record<'email' | 'phone', string>,
    placeholder: { email: 'e.g. tunde@gmail.com', phone: 'e.g. 0803 123 4567' } as Record<'email' | 'phone', string>,
    next: 'Next',
    back: 'Back',
    close: 'Close',
    save: {
      band: 'SAVE YOUR PROFILE',
      intro: 'We go send a code there to check say na you. Nobody else go see am.',
    },
    pin: {
      choose: 'Choose a PIN',
      note: '4 to 6 numbers wey you go remember. No 1234, no 0000.',
      again: 'Type the PIN again',
      mismatch: 'The two PINs no match. Choose am again.',
      yours: 'Your PIN',
      digits: (n: number, of: number) => `${n} of ${of} numbers`,
      clear: 'Clear',
      erase: 'Remove the last number',
    },
    code: {
      title: 'Put the code',
      sent: (hint: string) => `We don send a 6-number code to ${hint}. E go work for 10 minutes.`,
      resend: 'Send again',
      resent: 'New code don go.',
      label: 'The 6-number code',
    },
    saved: {
      band: 'PROFILE SAVED',
      line: (hint: string) => `Next time, on any phone, tap I don play before. Use ${hint} with your PIN.`,
      go: 'Continue',
    },
    signin: {
      open: 'I don play before',
      band: 'WELCOME BACK',
      intro: 'Use the email wey you save your profile with.',
      introPhone: 'Use the phone number wey you save your profile with.',
      go: 'Sign in',
      forgot: 'Forgot PIN?',
      welcome: (name: string) => `Welcome back, ${name}.`,
    },
    reset: {
      band: 'NEW PIN',
      intro: 'We go send a code to the email or phone wey you save your profile with.',
      send: 'Send code',
      sent: (hint: string) => `If ${hint} get profile with us, the code don go there. E go work for 10 minutes.`,
      choose: 'Choose a new PIN',
      note: 'Your other phones go sign out.',
      done: 'New PIN don set. You don sign in.',
    },
    manage: {
      band: 'SAVED PROFILE',
      savedWith: (hint: string) => `Saved with ${hint}. Use am with your PIN to sign in on any phone.`,
      guest: 'Only this phone knows you for now. Save your profile so you no lose am.',
      saved: 'Your profile don save. Sign in on any phone with your email and your PIN.',
      open: 'Manage',
      devices: 'Your phones',
      thisPhone: 'This phone',
      since: (d: string) => `Since ${d}`,
      signOutOthers: 'Sign out other phones',
      signedOut: (n: number) =>
        n === 0
          ? 'No other phone dey signed in.'
          : n === 1
            ? '1 other phone don sign out.'
            : `${n} other phones don sign out.`,
      delete: 'Delete my account',
    },
    remove: {
      band: 'DELETE ACCOUNT',
      line: 'Your name, coins, looks, stats and saved email or phone go comot for good. Rounds wey you play stay for the others. You no fit undo am.',
      pin: 'Put your PIN to delete',
      yes: 'Yes, delete am',
      no: 'Keep am',
      done: 'Your account don delete. Thank you for driving with us.',
    },
    err: {
      bad_contact: {
        email: 'That email no correct. Check am again.',
        phone: 'Put a Nigerian mobile number, like 0803 123 4567.',
      } as Record<'email' | 'phone', string>,
      pin_easy: 'That PIN too easy to guess. Choose another one.',
      pin_phone: 'No use the end of your phone number as PIN.',
      pin_length: 'Your PIN na 4 to 6 numbers.',
      wrong_code: (left: number) =>
        left > 1
          ? `That code no correct. ${left} more tries.`
          : left === 1
            ? 'That code no correct. 1 more try.'
            : 'That code no work again. Ask for a new one.',
      code_expired: 'The code don expire. Ask for a new one.',
      wrong_login: (kind: 'email' | 'phone', left: number) =>
        `${kind === 'email' ? 'Email' : 'Phone number'} or PIN no correct. ${left === 1 ? '1 more try' : `${left} more tries`} before we lock am small.`,
      locked: (minutes: number) =>
        minutes >= 120
          ? `Too many wrong PINs. Try again in ${Math.round(minutes / 60)} hours, or set a new PIN.`
          : `Too many wrong PINs. Try again in ${minutes} minutes, or set a new PIN.`,
      channel_off: 'We no fit send code there now. Try the other one.',
      already_saved: 'Your profile don already save.',
      email_only: 'Save your profile with your email.',
      contact_taken: 'Another profile don use that one. Sign in with am instead.',
      in_park: 'Comot from the park first, then delete.',
      confirm_needed: 'Tap Yes, delete am to delete.',
    },
  },
  /** Leaving a round in play (the leave rules): the round's menu, its confirm board, the toast after. */
  leave: {
    menu: 'This round',
    go: 'Leave round',
    stay: 'Stay',
    band: 'Leave this round?',
    /** What it costs, as the worker says (free, forfeit, fare). */
    free: 'The round go end for you. E no go cost you anything.',
    forfeit: 'E go count as loss. You no go get coins or challenge progress from this round.',
    fare: (fare: string) => `Your ${fare} fare go stay for the pot. The winner go carry am.`,
    /** What the leave mark brings, as the api says. */
    warning: 'Leave again today and you go wait before you join another park.',
    wait: (time: string) => `You go wait ${time} before you fit join another park.`,
    checking: 'We dey check your record…',
    /** The api didn't answer: what is always true, nothing guessed. */
    unknown: 'Leave rounds often and you go wait before you join another park.',
    /** The toast on the menu after. */
    left: 'You don leave the round.',
    leftLoss: 'You don leave the round. E count as loss.',
    minutes: (ms: number) => {
      const m = Math.ceil(ms / 60_000);
      return m === 1 ? '1 minute' : `${m} minutes`;
    },
  },
  /** Joining a park with people while the wait runs (the api refuses with leave_cooldown). */
  cooldown: {
    band: 'Hold on small',
    line: (clock: string) => `You fit join again in ${clock}`,
    note: 'You don leave too many rounds today. Every round wey you finish with people go clear one.',
    solo: 'Play alone',
    ok: 'OK',
    done: 'You fit join now.',
  },
  /** COMPETE AND WIN CASH: its strings live in compete-strings.ts, loaded with its screen (the shell budget). */
  compete: { loading: 'Dey open the competition…' },
  errors: {
    unknown_code: 'That code no dey.',
    code_used: 'You don use this code before.',
    code_ended: 'This code don expire.',
    code_used_up: 'Everybody don use this code finish.',
    not_for_you: 'This code no be for your account.',
    generic: 'We no fit finish that one. Check your network, then try again.',
    network: 'Network don cut. Trying again…',
    no_such_park: 'No park with that code.',
    unreachable: 'We no fit reach that park. Check your network, then try again.',
    park_full: 'That park don full.',
    match_on: 'That park don start already.',
    in_another_park: 'You still dey for another park.',
    no_worker: 'No space for new parks right now. Try again small time.',
    slow_down: 'Easy. Wait small, then try again.',
    session_invalid: 'Your Lagos Life session don expire. Open the game again.',
    name_too_short: 'Name too short.',
    nickname_taken: 'Somebody don take that nickname. Try another one.',
    replaced: 'You opened the park on another phone.',
    kicked: 'You can no longer enter this park.',
    host_kick: 'The host don remove you from that park. You no fit enter am again.',
    party_member_kicked: 'Somebody for your crew no fit enter that park.',
    not_enough_coins: 'You no get enough park money for that one.',
    coin_debt: 'Pay back the refunded park money first.',
    no_such_item: 'That item no dey the shop.',
    not_on_sale: 'That one no dey for sale again.',
    not_owned: 'Buy am first.',
    not_reached: 'Drive more rounds to reach that tier.',
    swap_used: 'You don swap one today already.',
    bad_park_name: 'Use 3 to 22 letters or numbers for the park name.',
    NO_TICKETS: 'You no get role ticket.',
    STAKE_PARK: 'Role tickets no work for stake parks.',
    challenge_done: 'That one don finish. Nothing to swap.',
    no_swap_left: 'No other challenge to swap in.',
    premium_locked: 'Open the premium track first.',
    season_over: 'This season don end.',
    no_such_season: 'That season no dey.',
    no_such_reward: 'That reward no dey.',
    not_wearable: 'Stickers no need that. Throw them for meetings.',
    refunded: 'That pack was refunded, so its park money don go.',
    not_your_purchase: 'That purchase belongs to another player.',
    unknown_product: 'We no sell that one. Talk to Lagos Life support.',
    lagos_life_off: 'Park money packs only work inside Lagos Life.',
    busy: 'The pay sheet is already open.',
    cancelled: 'No wahala. Nothing was paid.',
    payment_failed: 'The payment no go through. Nothing was paid.',
    rate_limited: 'Easy. Wait small, then try again.',
    not_framed: 'Stake tables open inside Lagos Life.',
    not_available: 'Stake tables never open for this game yet.',
    friendly_only: 'Lagos Life never approve stakes for this game yet.',
    insufficient_funds: 'Your Lagos Life balance no reach that fare.',
    limit_reached: 'You don reach today’s fare limit.',
    stake_banned: 'The park office don keep you off stake tables for now. Free parks still dey open for you.',
    stake_cap: 'You don put enough fare for today. Come back tomorrow, or play a free park.',
    stake_park: 'That park na stake table. Join it from Stake tables.',
    not_in_match: 'You no dey that table. Join it first.',
    no_such_match: 'That table no dey again.',
    match_over: 'That table don close.',
    bad_stake: 'That fare no be one of our tables.',
    bad_seats: 'Our stake tables seat 2 or 4.',
    race_stake_off: 'Race tables never open yet. Pick 4 seats for The Park.',
    stake_drawn: 'We no fit play that table, so our server post a draw. Every stake don go back.',
    playable: 'That one na real table. Join it from Stake tables.',
    park_gone: 'That table’s park don close.',
    lagos_life_only: 'Stake tables open inside Lagos Life.',
    try_again: 'Lagos Life no answer. Try again small time.',
    STAKE_NOT_READY: 'Wait for all 4 to put fare.',
    STAKE_DONE: 'This table don play its round.',
    no_such_party: 'No crew with that code.',
    party_full: 'That crew don full.',
    no_party: 'Start a crew first.',
    not_leader: 'Only the crew leader fit carry everybody.',
    not_recent: 'You fit only call drivers you played with.',
    party_member_busy: 'Somebody for your crew still dey another park.',
    type_off: 'That game dey rest small. Try another one.',
    TYPE_OFF: 'That game dey rest small. Try another one.',
    BOTS_AT_MONEY_TABLE: 'A stake table plays people only.',
    weather_taken: 'Somebody don buy the weather already.',
    weather_set: 'The host don set the weather for this park.',
    not_in_park: 'Enter the park first.',
    signed_out: 'This phone don sign out. Sign in again.',
    not_for_this_account: 'Your profile dey with the app wey you play from.',
  } as Record<string, string>,
} as const;
